import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable19 = ({ data }) => {
  const pdfRef = useRef();

  // ---------- 🔍 DATA ENHANCEMENT ----------
  const enhanced = data.map((row) => {
    const diff = row.year_2024 - row.year_2018;
    const pct = ((diff / row.year_2018) * 100).toFixed(2);
    return { ...row, diff, pct };
  });

  const avgGrowth =
    enhanced.reduce((sum, i) => sum + Number(i.pct), 0) / enhanced.length;

  const top = enhanced.reduce((a, b) =>
    Number(a.pct) > Number(b.pct) ? a : b,
  );

  // ---------- 📊 CHART DATA ----------
  const chartData = {
    labels: enhanced.map((i) => i.education_label),
    datasets: [
      {
        label: "2018",
        backgroundColor: "#F6CFFF",
        data: enhanced.map((i) => parseInt(i.year_2018)),
        borderRadius: 6,
      },
      {
        label: "2024",
        backgroundColor: "#2C92B8",
        data: enhanced.map((i) => parseInt(i.year_2024)),
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment by Education Level (Bachelor’s Degree or Advanced) — 2018–2024",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment Count" } },
      x: { title: { display: true, text: "Education Level" } },
    },
  };

  // ---------- 📄 PDF EXPORT ----------
 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text(APP_VERSION, 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("Education_Bachelors_Employment_2018_2024.pdf");
   });
 };

  return (
    <>
      {/* ---------- MAIN CARD ---------- */}
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-lg border border-gray-200"
      >
        <h2 className="text-center text-2xl font-bold text-gray-800 mb-6 leading-tight">
          Employment by Education Level <br />
          <span className="text-sm font-medium text-gray-500">
            (Bachelor’s Degree or Advanced — 2018–2024)
          </span>
        </h2>

        {/* ---------- KPI Cards ---------- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-100 text-center p-4 rounded-lg">
            <p className="font-semibold text-blue-800">📈 Avg Growth Rate</p>
            <p className="text-xl font-bold text-blue-700">
              {avgGrowth.toFixed(2)}%
            </p>
          </div>

          <div className="bg-green-100 text-center p-4 rounded-lg">
            <p className="font-semibold text-green-800">🚀 Fastest-Growing</p>
            <p className="font-bold">{top.education_label}</p>
            <p className="text-lg font-bold text-green-600">{top.pct}%</p>
          </div>

          <div className="bg-gray-100 text-center p-4 rounded-lg">
            <p className="font-semibold text-gray-700">🎓 Total Categories</p>
            <p className="text-xl font-bold">{enhanced.length}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ---------- TABLE ---------- */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-900 text-white uppercase text-sm">
                  <tr>
                    <th className="p-2">Education Level</th>
                    <th>2018</th>
                    <th>2024</th>
                    <th>Difference</th>
                    <th>Growth %</th>
                  </tr>
                </thead>
                <tbody>
                  {enhanced.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="p-2 font-medium">{row.education_label}</td>
                      <td>{Number(row.year_2018).toLocaleString("en-US")}</td>
                      <td>{Number(row.year_2024).toLocaleString("en-US")}</td>
                      <td className="font-semibold text-blue-700">
                        {Number(row.diff).toLocaleString("en-US")}
                      </td>
                      <td className="font-semibold text-green-600">
                        {row.pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---------- CHART ---------- */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* ---------- PDF BUTTON ---------- */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable19;

